<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

import { MapSource, MapLayer } from '@/components/map'
import { getBaseUrl } from '@/utils/url'
import { useLayerManager } from '@/composables/useLayerManager'
import { createLayerId, createSourceId } from '@/components/layers/types'

const store = useStore()
const map = computed(() => store.getters['geo/getMap'])
const layerManager = useLayerManager(map)

const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  mosaics: {
    type: Array, // Only monthly array
    required: true
  },
  layerId: {
    type: String,
    required: true
  },
  beforeId: {
    type: String,
    required: true
  },
  groupId: {
    type: String,
    required: true
  },
  orgId: {
    type: Number,
    required: true
  },
  options: {
    type: Object,
    default: () => ({})
  },
  showControls: {
    type: Boolean,
    default: true
  }
})

const host = getBaseUrl()

const monthNames = [
  'January', 'February', 'March',
  'April', 'May', 'June',
  'July', 'August', 'September',
  'October', 'November', 'December'
]

const monthlyAvailableMonthsByYear = ref({})
const monthlyYearOptions = ref([])
const monthlySelectedYear = ref(null)
const monthlySelectedMonth = ref(null)
const monthlyLastLayedId = ref(null)
const currentRegisteredLayerId = ref(null)

// Helper functions for unified layer IDs
const getMosaicSourceId = (mosaicName) => createSourceId(props.groupId, `planet-${mosaicName}-source`)
const getMosaicLayerId = (mosaicName) => createLayerId(props.groupId, `planet-${mosaicName}`)

onMounted(() => {
  props.mosaics.forEach(m => {
    const date = new Date(m.first_acquired)
    const y = date.getUTCFullYear()
    const mo = date.getUTCMonth() + 1 // 1..12
    if (!monthlyAvailableMonthsByYear.value[y]) {
      monthlyAvailableMonthsByYear.value[y] = new Set()
    }
    monthlyAvailableMonthsByYear.value[y].add(mo)
  })

  const numericYears = Object.keys(monthlyAvailableMonthsByYear.value)
    .map(y => parseInt(y, 10))
    .sort((a, b) => a - b)

  monthlyYearOptions.value = numericYears.map(y => String(y))

  // Initialize from store selection if available, otherwise default to first mosaic
  if (props.mosaics.length) {
    const stored = store.getters['layers/getPlanetscopeSelection'](props.layerId)
    if (stored && stored.tab === 'monthly' && stored.year && stored.month) {
      monthlySelectedYear.value = stored.year
      monthlySelectedMonth.value = stored.month
    } else {
      const defaultMosaic = props.mosaics[0]
      const defaultDate = new Date(defaultMosaic.first_acquired)
      monthlySelectedYear.value = String(defaultDate.getUTCFullYear())
      monthlySelectedMonth.value = String(defaultDate.getUTCMonth() + 1)
    }
    monthlyLastLayedId.value = getMosaicLayerId(monthlySelectedMosaic.value.name)

    // Register the initial layer with layer manager
    currentRegisteredLayerId.value = monthlyLastLayedId.value
    layerManager.registerLayer(props.groupId, currentRegisteredLayerId.value)
  }
})

onUnmounted(() => {
  // Unregister current layer from layer manager
  if (currentRegisteredLayerId.value) {
    layerManager.unregisterLayer(props.groupId, currentRegisteredLayerId.value)
  }
})

const monthOptionsForSelectedYear = computed(() => {
  if (!monthlySelectedYear.value) return []
  const monthsSet = monthlyAvailableMonthsByYear.value[Number(monthlySelectedYear.value)]
  if (!monthsSet) return []
  return [...monthsSet].sort((a, b) => a - b).map(m => String(m))
})

const monthlySelectedMosaic = computed(() => {
  const selYear = Number(monthlySelectedYear.value)
  const selMonth = Number(monthlySelectedMonth.value)

  return props.mosaics.find(m => {
    const date = new Date(m.first_acquired)
    return (
      date.getUTCFullYear() === selYear &&
      (date.getUTCMonth() + 1) === selMonth
    )
  }) || props.mosaics[0]
})

watch([monthlySelectedYear, monthlySelectedMonth], () => {
  if (monthlySelectedMosaic.value) {
    const newLayerId = getMosaicLayerId(monthlySelectedMosaic.value.name)

    // Update layer manager registration when mosaic changes
    if (currentRegisteredLayerId.value && currentRegisteredLayerId.value !== newLayerId) {
      layerManager.unregisterLayer(props.groupId, currentRegisteredLayerId.value)
    }
    layerManager.registerLayer(props.groupId, newLayerId)
    currentRegisteredLayerId.value = newLayerId

    monthlyLastLayedId.value = newLayerId
  }
})

// Watch for selection changes from the detail page via store
const storeSelection = computed(() => store.getters['layers/getPlanetscopeSelection'](props.layerId))
watch(storeSelection, (selection) => {
  if (!selection) return
  if (selection.year && selection.year !== monthlySelectedYear.value) {
    monthlySelectedYear.value = selection.year
  }
  if (selection.month && selection.month !== monthlySelectedMonth.value) {
    monthlySelectedMonth.value = selection.month
  }
})
</script>

<template>
  <div v-if="mosaics.length">
    <div v-if="showControls" class="pointer-events-auto">
      <div class="flex space-x-1 -mr-1">
        <Select
          v-if="monthlyYearOptions.length"
          v-model="monthlySelectedYear"
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a year" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem
                v-for="(year, index) in monthlyYearOptions"
                :key="index"
                :value="year"
              >
                {{ year }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
        <Select
          v-if="monthOptionsForSelectedYear.length"
          v-model="monthlySelectedMonth"
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a month" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem
                v-for="(month, index) in monthOptionsForSelectedYear"
                :key="index"
                :value="month"
              >
                {{ monthNames[Number(month) - 1] }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </div>

    <div v-if="map">
      <div v-for="mosaic in mosaics" :key="mosaic.name">
        <MapSource
          v-if="monthlyLastLayedId === getMosaicLayerId(mosaic.name)"
          :map="map"
          type="raster"
          :id="getMosaicSourceId(mosaic.name)"
          :options="{
            tiles: [`${host}/tulum/tiles/planetscope/${orgId}/${mosaic.name}/{z}/{x}/{y}.png`],
            tileSize: 256
          }"
        >
          <MapLayer
            :map="map"
            :id="getMosaicLayerId(mosaic.name)"
            type="raster"
            :source="getMosaicSourceId(mosaic.name)"
            :before-id="beforeId"
            :group-id="groupId"
            :options="options"
          />
        </MapSource>
      </div>
    </div>
  </div>
</template>
